import * as Select from "@radix-ui/react-select";

import type { TokenDef } from "../data/tokens.js";

import styles from "../styles/RichSelect.module.css";
import { TokenIcon } from "./TokenIcon.js";

/**
 * Radix-based rich select for tokens. Renders logo + symbol + name + price
 * + decimals per row. Trigger shows logo + symbol + price.
 *
 * The `disabledAddress` prop lets the caller block a specific token (used
 * to forbid selecting the same address in both the `from` and `to` slots).
 */
export interface TokenSelectProps {
  readonly value: TokenDef;
  readonly tokens: readonly TokenDef[];
  readonly onChange: (address: string) => void;
  readonly disabledAddress?: string;
  readonly ariaLabel?: string;
}

function formatPriceUSD(price: string | undefined): string {
  if (price === undefined || price === "") return "";
  const num = Number(price);
  if (!Number.isFinite(num)) return "";
  if (num >= 1) return `≈ $${num.toFixed(2)}`;
  if (num >= 0.01) return `≈ $${num.toFixed(4)}`;
  return `≈ $${num.toPrecision(2)}`;
}

export function TokenSelect({
  value,
  tokens,
  onChange,
  disabledAddress,
  ariaLabel,
}: TokenSelectProps): React.JSX.Element {
  const triggerPrice = formatPriceUSD(value.priceUSD);
  const disabledLower = disabledAddress?.toLowerCase();

  return (
    <Select.Root value={value.address} onValueChange={onChange}>
      <Select.Trigger
        className={styles.trigger}
        aria-label={ariaLabel ?? "Token"}
      >
        <TokenIcon logoURI={value.logoURI} symbol={value.symbol} />
        <span className={styles.triggerLabel}>
          <span className={styles.triggerPrimary}>{value.symbol}</span>
          <span className={styles.triggerSecondary}>
            {value.name}
            {triggerPrice !== "" ? ` · ${triggerPrice}` : ""}
          </span>
        </span>
        <Select.Icon className={styles.triggerChevron}>▾</Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className={styles.content}
          position="popper"
          sideOffset={4}
        >
          <Select.Viewport className={styles.viewport}>
            {tokens.map((t) => {
              const price = formatPriceUSD(t.priceUSD);
              const isDisabled =
                disabledLower !== undefined &&
                t.address.toLowerCase() === disabledLower;
              return (
                <Select.Item
                  key={t.address}
                  value={t.address}
                  className={styles.item}
                  disabled={isDisabled}
                >
                  <TokenIcon
                    logoURI={t.logoURI}
                    symbol={t.symbol}
                    size="item"
                  />
                  <span className={styles.itemBody}>
                    <Select.ItemText asChild>
                      <span className={styles.itemPrimary}>{t.symbol}</span>
                    </Select.ItemText>
                    <span className={styles.itemSecondary}>{t.name}</span>
                  </span>
                  <span className={styles.itemMeta}>
                    {price !== "" ? (
                      <span className={styles.itemPrice}>{price}</span>
                    ) : null}
                    <span className={styles.itemDecimals}>
                      {t.decimals} dec
                    </span>
                  </span>
                </Select.Item>
              );
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
